import React, { useState, useEffect } from "react";
import styles from "./KnowledgeBase.module.css";
import { Spinner } from "react-bootstrap";
// Knowledge Base Grid Component
export const KnowledgeBaseGrid = ({
	onCreateNew,
	onEdit,
	knowledgeBases,
	isLoading,
	error,
}) => {
	if (isLoading) {
		return (
			<div className={styles.loadingContainer}>
				<p className={styles.loadingText}>Loading knowledge bases...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.emptyState}>
				<p className={styles.errorText}>{error}</p>
			</div>
		);
	}

	return (
		<div>
			<div className={styles.grid}>
				<CreateKnowledgeBaseButton onClick={onCreateNew} />
				{knowledgeBases?.length === 0 ? (
					<p>No knowledge bases found. Create your first one to get started.</p>
				) : (
					knowledgeBases?.map((kb) => (
						<KnowledgeBaseCard
							key={kb.id}
							knowledgeBase={kb}
							onEdit={() => onEdit(kb)}
						/>
					))
				)}
			</div>
		</div>
	);
};

// Knowledge Base Card Component
const KnowledgeBaseCard = ({ knowledgeBase, onEdit }) => {
	const { name, createdAt } = knowledgeBase;

	const formatDate = (timestamp) => {
		if (!timestamp) return "No date available";

		// Handle Firestore Timestamp object with _seconds
		if (timestamp && timestamp._seconds) {
			const date = new Date(timestamp._seconds * 1000);
			const month = date.toLocaleString("en-US", { month: "short" });
			const day = date.getDate();
			const time = date.toLocaleString("en-US", {
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			});

			return `${month} ${day} at ${time}`;
		}

		// Handle regular Date object
		if (timestamp instanceof Date) {
			const month = timestamp.toLocaleString("en-US", { month: "short" });
			const day = timestamp.getDate();
			const time = timestamp.toLocaleString("en-US", {
				hour: "numeric",
				minute: "2-digit",
				hour12: true,
			});

			return `${month} ${day} at ${time}`;
		}

		return "Invalid Date";
	};

	return (
		<div className={styles.card} onClick={onEdit}>
			<div className={styles.cardHeader}>
				<div className={styles.cardLabel}>Name</div>
				<h3 className={styles.cardTitle}>{name}</h3>
			</div>
			<div className={styles.cardFooter}>
				<div className={styles.cardLabel}>Creation Date</div>
				<div className={styles.cardDate}>{formatDate(createdAt)}</div>
			</div>
		</div>
	);
};

// Create Button Component
const CreateKnowledgeBaseButton = ({ onClick }) => {
	return (
		<div className={styles.createButton} onClick={onClick}>
			<div className={styles.createIcon}>+</div>
			<p className={styles.createText}>Create New</p>
		</div>
	);
};

// Knowledge Base Modal Component
export const KnowledgeBaseModal = ({
	isOpen,
	knowledgeBase,
	onClose,
	onSave,
	createKnowledgeBase,
	updateKnowledgeBase,
	showDropdown,
	fetchUserOrganizations,
	userOrganizations,
}) => {
	const [name, setName] = useState(knowledgeBase?.name || "New Knowledge Base");
	const [intro, setIntro] = useState(knowledgeBase?.intro || "");
	const [instructions, setInstructions] = useState(
		knowledgeBase?.instructions || ""
	);

	const [orgId, setOrgId] = useState("");

	const [showGuidanceModal, setShowGuidanceModal] = useState(false);
	const [showTipsPopup, setShowTipsPopup] = useState(false);
	const [isClosingTipsPopup, setIsClosingTipsPopup] = useState(false);
	const [error, setError] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	// State for minimized popup
	const [isMinimized, setIsMinimized] = useState(false);

	// Tips popup toggle functions
	const openTipsPopup = () => {
		setIsClosingTipsPopup(false);
		setShowTipsPopup(true);
		setIsMinimized(false); // Reset minimize state when opening
	};

	const closeTipsPopup = () => {
		setIsClosingTipsPopup(true);
		// Wait for animation to complete before removing from DOM
		setTimeout(() => {
			setShowTipsPopup(false);
			setIsClosingTipsPopup(false);
		}, 300); // Match this with animation duration
	};

	const handleToggleTipsPopup = () => {
		if (showTipsPopup && !isClosingTipsPopup) {
			closeTipsPopup();
		} else {
			openTipsPopup();
		}
	};

	// Toggle between minimized and maximized state
	const toggleMinimize = () => {
		setIsMinimized(!isMinimized);
	};

	// Reset form when modal opens/closes or knowledge base changes
	useEffect(() => {
		if (isOpen) {
			if (knowledgeBase) {
				// Edit mode - prefill form
				setName(knowledgeBase.name);
				setIntro(knowledgeBase.intro || "");
				setInstructions(knowledgeBase.instructions || "");
				setOrgId(knowledgeBase.orgId || "");
			} else {
				// Create mode - set default values
				fetchUserOrganizations();
				setName("New Knowledge Base");
				setIntro("");
				setInstructions("");
			}
			setError("");
			setShowGuidanceModal(false);
		}
	}, [isOpen, knowledgeBase, fetchUserOrganizations]);

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validate form
		if (!name) {
			setError("Name is required");
			return;
		}

		setIsSaving(true);

		try {
			// Call the appropriate API function based on whether we're editing or creating
			let result;

			const knowledgeBaseData = {
				name,
				intro,
				instructions,
				orgId,
			};

			if (knowledgeBase) {
				result = await updateKnowledgeBase(knowledgeBase.id, knowledgeBaseData);
			} else {
				// Create new knowledge base
				result = await createKnowledgeBase(knowledgeBaseData);
			}

			if (result) {
				onSave && onSave(result);
				onClose();
			} else {
				setError("Failed to save knowledge base");
			}
		} catch (err) {
			setError("An error occurred while saving");
			console.error("Knowledge base save error:", err);
		} finally {
			setIsSaving(false);
		}
	};

	/*const handleRewriteWithGuidance = () => {
    // Show the guidance modal
    setShowGuidanceModal(true);
  };*/

	const handleGuidancePreview = () => {
		// Update the full prompt with the generated content
		// Close the guidance modal
		setShowGuidanceModal(false);
	};

	if (!isOpen) return null;

	return (
		<div className={styles.modalOverlay}>
			<div className={styles.modal}>
				{!showGuidanceModal ? (
					// Main Knowledge Base Form
					<>
						<div className={styles.modalHeader}>
							<h2 className={styles.modalTitle}>
								{knowledgeBase ? knowledgeBase.name : "New Knowledge Base"}
							</h2>
							<div className={styles.headerButtons}>
								<button
									className={styles.tipsButton}
									title="Tips"
									onClick={handleToggleTipsPopup}
								>
									<i className="far fa-lightbulb"></i> Tips
								</button>
								<button className={styles.copyButton} title="Copy">
									<i className="far fa-copy"></i>
								</button>
								<button className={styles.closeButton} onClick={onClose}>
									×
								</button>
							</div>
						</div>

						<form className={styles.form} onSubmit={handleSubmit}>
							{isSaving ? (
								<div className={styles.loadingContainer}>
									<Spinner animation="border" size="sm" />
									<p className={styles.loadingText}>Saving...</p>
								</div>
							) : (
								<>
									<div className={styles.formGroup}>
										<label className={styles.label} htmlFor="name">
											Name
										</label>
										<input
											id="name"
											className={styles.input}
											type="text"
											value={name}
											onChange={(e) => setName(e.target.value)}
											placeholder="New Knowledge Base"
										/>
									</div>

									<div className={styles.formGroup}>
										<div className={styles.labelWithAction}>
											<label className={styles.label} htmlFor="intro">
												Opening intro
											</label>
										</div>
										<textarea
											id="intro"
											className={styles.textarea}
											value={intro}
											onChange={(e) => setIntro(e.target.value)}
											placeholder="Write a quick intro for your avatar to speak for greeting people when starting the chat"
										/>
									</div>

									<div className={styles.formGroup}>
										<div className={styles.labelWithAction}>
											<label className={styles.label} htmlFor="instructions">
												Knowledge Data
											</label>
										</div>
										<textarea
											id="instructions"
											className={styles.textarea}
											value={instructions}
											onChange={(e) => setInstructions(e.target.value)}
											placeholder="Please add the knowledge data you want your avatar to know. The knowledge base can be specific information, Q&A examples, or any other info"
										/>
									</div>
									{showDropdown && (
										<div className={styles.labelWithAction}>
											<label className={styles.label} htmlFor="orgId">
												Organization
											</label>
											<select
												className={styles.select}
												as="select"
												value={orgId}
												onChange={(e) => setOrgId(e.target.value)}
												required
											>
												<option value="">Select an organization</option>
												{userOrganizations.map((org) => (
													<option key={org.orgId} value={org.orgId}>
														{org.name}
													</option>
												))}
											</select>
										</div>
									)}
								</>
							)}

							{error && <p className={styles.errorText}>{error}</p>}

							<div className={styles.modalFooter}>
								<button
									type="button"
									className={`${styles.button} ${styles.cancelButton}`}
									onClick={onClose}
									disabled={isSaving}
								>
									Cancel
								</button>
								<button
									type="submit"
									className={`${styles.button} ${styles.saveButton}`}
									disabled={isSaving}
								>
									{isSaving ? "Saving..." : "Save and apply"}
								</button>
							</div>
						</form>
					</>
				) : (
					// Guidance Form
					<>
						<div className={styles.modalHeader}>
							<button
								className={styles.backButton}
								onClick={() => setShowGuidanceModal(false)}
							>
								{"<"}
							</button>
							<h2 className={styles.modalTitle}>
								Rewrite {name || "Default Knowledge Base"} with guidance
							</h2>
							<div className={styles.headerButtons}>
								<button
									className={styles.tipsButton}
									title="Tips"
									onClick={handleToggleTipsPopup}
								>
									<i className="far fa-lightbulb"></i> Tips
								</button>
							</div>
						</div>

						<div className={styles.form}>
							<div className={styles.formGroup}>
								<label className={styles.label}>Instructions</label>
								<textarea
									className={styles.textarea}
									value={instructions}
									onChange={(e) => setInstructions(e.target.value)}
									placeholder="Provide any instructions for how the Interactive Avatar should act in its conversations with users. Should it limit all conversation to only certain subjects, for example, the knowledge you have provided?"
								/>
							</div>

							<div className={styles.modalFooter}>
								<button
									type="button"
									className={`${styles.button} ${styles.cancelButton}`}
									onClick={() => setShowGuidanceModal(false)}
								>
									Cancel
								</button>
								<button
									type="button"
									className={`${styles.button} ${styles.previewButton}`}
									onClick={handleGuidancePreview}
								>
									Preview full prompt
								</button>
							</div>
						</div>
					</>
				)}
			</div>

			{/* Tips Popup */}
			{showTipsPopup && (
				<div
					className={`${styles.tipsPopupOverlay} ${
						isClosingTipsPopup ? styles.fadeOut : styles.fadeIn
					}`}
					onClick={handleToggleTipsPopup}
				>
					<div
						className={`${styles.tipsPopup} ${
							isClosingTipsPopup ? styles.scaleOut : styles.scaleIn
						} ${isMinimized ? styles.minimizedPopup : ""}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className={styles.tipsPopupHeader}>
							<button
								className={styles.backArrow}
								onClick={handleToggleTipsPopup}
								aria-label="Close tips"
							>
								<i className="fas fa-arrow-left"></i>
							</button>
							<h3>Tips for Effective Knowledge Base Setup</h3>
							<div className={styles.headerButtonsGroup}>
								<button
									className={styles.sizeToggleButton}
									onClick={toggleMinimize}
									aria-label={isMinimized ? "Maximize" : "Minimize"}
								>
									{isMinimized ? (
										<i className="fas fa-expand"></i>
									) : (
										<i className="fas fa-compress"></i>
									)}
								</button>
							</div>
						</div>
						<div className={styles.tipsPopupContent}>
							<section className={styles.tipsSection}>
								<h4>Core Instructions</h4>
								<p>
									Preset instructions are already set up for optimal responses
									and the persona of the avatar. For example:
								</p>
								<p>
									Replies are limited in length to be professional and concise
								</p>
								<p>
									Tone is professional and replies are strictly based on the
									knowledge data provided
								</p>
							</section>

							<section className={styles.tipsSection}>
								<h4>Knowledge Section</h4>
								<p>
									List key data or reference points your avatar can draw on.
								</p>
								<p>Treat these as the "facts" it can access—like a database.</p>
								<p>Prodvide Q&A examples from your website</p>
								<p>
									Copy and paste document contents and website copy. The avatar
									will also use public knowledge of your company if it is
									available.
								</p>
								<p>Example for the RATAVA website:</p>
								<blockquote>
									"What are the RATAVA use cases? RATAVAs can be used for
									anything related to visual media and communication.
									Personalized emails, internal communication, social media
									creation, media language localization, training and compliance
									videos"
								</blockquote>
							</section>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
